import { useCallback, useEffect, useRef } from 'react';

import { useCheck } from 'Src/feature/check/useCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useToast } from 'Src/feature/toasts/useToast';
import useRoute from 'Src/routes/useRoute';

import { useTerminal } from './useTerminal';
import { useTerminalDefaults } from './useTerminalDefaults';

const sectionId = 'useTerminalSettings';

// "terminalSettings.defaultView" navigation to
//  - based on "terminalSettings.defaultView
//    - data loading
//    - data set up
//    - routing
//    - redirecting
const useDefaultView_goTo = () => {
  const log = useLogger().getSubLogger({ name: `${sectionId}.defaultView.navigate.to` });
  const { navigate, crumbs, route } = useRoute();
  const toast = useToast({ persist: true });
  const createCheck = useCheck.mutate.create();
  const terminalDefaults = useTerminalDefaults();
  const isCreatingCheck = useRef(false);

  const defaultViewNavigateTo = useCallback(async () => {
    if (!terminalDefaults.loading) {
      if (terminalDefaults.view === 'CheckList') {
        navigate(crumbs.pos.checks.main.interpolate({
          tabId: route.params.value.checksList,
        }));
      } else if (terminalDefaults.view === 'FloorPlan') {
        navigate(crumbs.pos.checks.main.interpolate({
          tabId: route.params.value.checksFloorPlan,
        }));
      } else if (terminalDefaults.view === 'NewCheck') {
        const { revenueCenterId, orderTypeId, partySize, partySizeType, startTabWithCard } = terminalDefaults;
        if (partySizeType === 'Required' || startTabWithCard === 'Enabled') {
          navigate(crumbs.pos.checks.main.interpolate({
            tabId: route.params.value.checksList,
          }));
        } else if (revenueCenterId && orderTypeId && partySize) {
          if (!isCreatingCheck.current) {
            const createNewCheck = async () => {
              log.debug('createNewCheck()', 'function first called', 'set isCreatingCheck ref to true', { stack: (new Error()).stack });
              isCreatingCheck.current = true;
              try {
                const payload = {
                  check: {
                    revenueCenterId,
                    orderTypeId,
                    partySize
                  }
                };
                log.debug('createNewCheck()', 'createCheck.mutateAsync() kicking off with payload:', { payload, stack: (new Error()).stack });
                const response = await createCheck.mutateAsync(payload);
                if (terminalDefaults.checkNameInputType === 'Optional' || terminalDefaults.checkNameInputType === 'Required') {
                  log.debug('createNewCheck()', 'check created', 'additional input needed from user', 'navigate to created check with check details modal open');
                  navigate(crumbs.pos.check.detail.interpolate({
                    checkId: response.id,
                    itemSelect: route.params.value.one,
                    tabId: 'check-information',
                  }));
                } else {
                  log.debug('createNewCheck()', 'check created', 'no additional input needed from user', 'navigate to created check');
                  navigate(crumbs.pos.check.main.interpolate({
                    checkId: response.id,
                    itemSelect: route.params.value.one,
                  }));
                }
              } catch (err) {
                log.error(terminalDefaults.view, 'createCheck fail', err);

                toast.show.serviceError(err, { id: `${sectionId}.useDefaultView_goTo` });
                navigate(crumbs.pos.checks.main.interpolate({
                  tabId: route.params.value.checksList,
                }));
              }
            };
            void createNewCheck();
          }
        } else {
          log.info('navigate.to', terminalDefaults.view, 'data not ready', {
            revenueCenterId,
            orderTypeId,
            partySize,
            partySizeType,
            startTabWithCard,
          });
        }
      } else {
        log.info('navigate.to', 'terminalDefaults not defined so redirect');
        // we should not have got here w/out "terminalSettings.defaultView"
        // - redirect back to the check list (default view)
        navigate(crumbs.pos.checks.main.interpolate({
          tabId: route.params.value.checksList,
        }));
      }
    }
  }, [
    createCheck,
    crumbs.pos.check.main,
    crumbs.pos.check.detail,
    crumbs.pos.checks.main,
    log,
    navigate,
    route.params.value.checksFloorPlan,
    route.params.value.checksList,
    route.params.value.one,
    terminalDefaults,
    toast.show,
  ]);

  //  - kind of gross, I know, but... at least we can colocate the logic for navigating based on "terminalSettings.defaultView"
  //  - stable function
  //    - can be called multiple times from a "useEffect"
  //    - should route and/or create a new check based on "terminalSettings.defaultView" configuration
  useEffect(() => {
    void defaultViewNavigateTo();
  }, [defaultViewNavigateTo]);
};


// "terminalSettings.defaultView" navigation back
//  - return function based on "terminalSettings.defaultView
//    - data loading
//    - routing
const useDefaultView_goBack = () => {
  const { navigate, crumbs, route } = useRoute();
  const terminalDefaults = useTerminalDefaults();
  return useCallback(() => {
    switch (terminalDefaults.view) {
      case 'CheckList': {
        navigate(crumbs.pos.checks.main.interpolate({
          tabId: route.params.value.checksList,
        }));
        break;
      }
      case 'FloorPlan': {
        navigate(crumbs.pos.checks.main.interpolate({
          tabId: route.params.value.checksFloorPlan,
        }));
        break;
      }
      case 'NewCheck': {
        // TODO: what should be the go back case here ??
        navigate(crumbs.pos.checks.main.interpolate({
          tabId: route.params.value.checksList,
        }));
        break;
      }
      default: {
        // we should not have got here w/out "terminalSettings.defaultView"
        // - redirect back to the check list (default view)
        navigate(crumbs.pos.checks.main.interpolate({
          tabId: route.params.value.checksList,
        }));
        break;
      }
    }
  }, [crumbs.pos.checks.main, navigate, route.params.value.checksFloorPlan, route.params.value.checksList, terminalDefaults]);
};

const useTerminalSettings = {
  query: useTerminal.query.settings,
  defaultView: {
    navigate: {
      to: useDefaultView_goTo,
      back: useDefaultView_goBack,
    },
  },
};

export { useTerminalSettings };
